import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, PencilSimple, Trash, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const SPECIALTIES = ["Flyer", "Logo", "Seal", "Banner", "Web", "Video", "Branding"];

export default function AdminDesigners() {
  const [designers, setDesigners] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/designers");
      setDesigners(data);
    } catch (_) {}
  };

  useEffect(() => {
    load();
  }, []);

  const startCreate = () => {
    setEditing({ name: "", email: "", password: "", specialties: [], notes: "", active: true });
    setShowForm(true);
  };

  const startEdit = (d) => {
    setEditing({ ...d, password: "" });
    setShowForm(true);
  };

  const save = async () => {
    try {
      if (editing.id) {
        const update = {
          name: editing.name,
          specialties: editing.specialties,
          notes: editing.notes,
          active: editing.active,
        };
        if (editing.password) update.password = editing.password;
        await api.patch(`/admin/designers/${editing.id}`, update);
        toast.success("Designer updated");
      } else {
        await api.post("/admin/designers", {
          name: editing.name,
          email: editing.email,
          password: editing.password,
          specialties: editing.specialties,
          notes: editing.notes,
        });
        toast.success("Designer added & login emailed");
      }
      setShowForm(false);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this designer?")) return;
    try {
      await api.delete(`/admin/designers/${id}`);
      toast.success("Designer deactivated");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleSpec = (s) => {
    setEditing((e) => ({
      ...e,
      specialties: e.specialties.includes(s)
        ? e.specialties.filter((x) => x !== s)
        : [...e.specialties, s],
    }));
  };

  return (
    <AdminLayout>
      <div className="mb-8 flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Team</div>
          <h1 className="font-display text-5xl mt-2">Designers</h1>
        </div>
        <button
          onClick={startCreate}
          data-testid="add-designer"
          className="btn-primary px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2"
        >
          <Plus size={16} weight="bold" /> Add designer
        </button>
      </div>

      <div className="border border-[#262626] divide-y divide-[#262626]" data-testid="designers-list">
        <div className="hidden md:grid grid-cols-12 gap-4 p-4 bg-[#0f0f0f] text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3]">
          <div className="col-span-3">Name</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-3">Specialties</div>
          <div className="col-span-1">Active</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {designers.map((d) => (
          <div key={d.id} className="grid grid-cols-12 gap-4 p-4 bg-[#0a0a0a] items-center" data-testid={`designer-${d.id}`}>
            <div className="col-span-12 md:col-span-3 font-semibold">{d.name}</div>
            <div className="col-span-6 md:col-span-3 text-sm text-[#a3a3a3] truncate">{d.email || "—"}</div>
            <div className="col-span-6 md:col-span-3 flex flex-wrap gap-1">
              {(d.specialties || []).map((s) => (
                <span key={s} className="text-[10px] uppercase tracking-wider bg-[#1a1a1a] border border-[#262626] px-2 py-0.5">{s}</span>
              ))}
            </div>
            <div className="col-span-4 md:col-span-1 text-sm font-bold text-[#FF2D2D]">{d.active_projects}</div>
            <div className="col-span-4 md:col-span-1 text-xs uppercase tracking-wider">
              {d.active ? <span className="text-[#FF2D2D]">Active</span> : <span className="text-[#a3a3a3]">Inactive</span>}
            </div>
            <div className="col-span-4 md:col-span-1 flex justify-end gap-2">
              <button
                onClick={() => startEdit(d)}
                data-testid={`edit-designer-${d.id}`}
                className="btn-outline px-2 py-1 text-xs"
              >
                <PencilSimple size={12} />
              </button>
              {d.active && (
                <button
                  onClick={() => deactivate(d.id)}
                  data-testid={`deactivate-designer-${d.id}`}
                  className="btn-outline px-2 py-1 text-xs"
                >
                  <Trash size={12} />
                </button>
              )}
            </div>
          </div>
        ))}
        {designers.length === 0 && (
          <div className="bg-[#0a0a0a] p-8 text-center text-[#a3a3a3]">
            No designers yet. Add your first one to start assigning projects.
          </div>
        )}
      </div>

      {showForm && editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="designer-modal">
          <div className="border border-[#262626] bg-[#0a0a0a] w-full max-w-lg p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">{editing.id ? "Edit" : "New"}</div>
                <h3 className="font-display text-2xl mt-1">{editing.id ? "Edit designer" : "Add designer"}</h3>
              </div>
              <button onClick={() => setShowForm(false)} className="text-[#a3a3a3] hover:text-white"><X size={18} /></button>
            </div>

            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-3 mb-2">Name</label>
            <input
              type="text"
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              data-testid="designer-name"
              className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
            />

            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-4 mb-2">
              Email{editing.id ? " (locked)" : ""}
            </label>
            <input
              type="email"
              value={editing.email || ""}
              onChange={(e) => setEditing({ ...editing, email: e.target.value })}
              data-testid="designer-email"
              disabled={!!editing.id}
              className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none disabled:opacity-50"
            />

            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-4 mb-2">
              {editing.id ? "Reset password (optional)" : "Initial password"}
            </label>
            <input
              type="text"
              value={editing.password || ""}
              onChange={(e) => setEditing({ ...editing, password: e.target.value })}
              data-testid="designer-password"
              placeholder={editing.id ? "Leave blank to keep current" : "Min 6 characters"}
              className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none font-mono"
            />
            {!editing.id && (
              <div className="text-xs text-[#a3a3a3] mt-1">
                We'll email these credentials to the designer.
              </div>
            )}

            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-4 mb-2">Specialties</label>
            <div className="flex flex-wrap gap-2">
              {SPECIALTIES.map((s) => {
                const active = editing.specialties.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleSpec(s)}
                    data-testid={`spec-${s}`}
                    className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${
                      active ? "border-[#FF2D2D] bg-[#FF2D2D] text-white" : "border-[#262626] text-[#a3a3a3]"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>

            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-4 mb-2">Notes</label>
            <textarea
              value={editing.notes || ""}
              onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
              data-testid="designer-notes"
              rows={2}
              className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
            />

            {editing.id && (
              <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={editing.active}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                  data-testid="designer-active"
                />
                Active (available for assignments)
              </label>
            )}

            <div className="flex gap-3 mt-6">
              <button
                onClick={save}
                disabled={!editing.name || !editing.email || (!editing.id && (!editing.password || editing.password.length < 6))}
                data-testid="save-designer"
                className="btn-primary flex-1 px-4 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-50"
              >
                Save
              </button>
              <button
                onClick={() => setShowForm(false)}
                data-testid="cancel-designer"
                className="btn-outline flex-1 px-4 py-3 text-sm uppercase tracking-wider"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
